import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { DealsMap } from '@/components/Deals/DealsMap';
import { GoogleMapsWrapper } from '@/components/Deals/GoogleMapsWrapper';
import { DealsDesktopLayout } from '@/components/Deals/Layout/DealsDesktopLayout';
import { DealsMobileLayout } from '@/components/Deals/Layout/DealsMobileLayout';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingScreen } from '@/components/Layout/LoadingScreen';
import { MuiBox } from '@/components/mui';
import { GoogleMapsContext } from '@/hooks/useGoogleMaps';
import { type Deal, useInfiniteDealsQuery } from '@/hooks/useInfiniteDealsQuery';
import { useAuth } from '@/lib/authClient';
import { useGeolocation } from '@/providers/GeolocationProvider';
import { useDealsStoreActions } from '@/stores/dealsStore';
import { showError } from '@/stores/errorStore';
import { useUiIsMobile, useUiSidebarOpen } from '@/stores/uiStore';
import { createLogger } from '@/utils/logger';

const logger = createLogger('DealsMapPage');

export const Route = createFileRoute('/deals')({
  component: DealsMapPage,
});

function DealsMapPage() {
  const isMobile = useUiIsMobile();

  // Auth state
  const { isAuthenticated, isPending } = useAuth();

  const { setSelectedDeal } = useDealsStoreActions();

  // UI state
  const sidebarOpen = useUiSidebarOpen();

  // Map instance state for context
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [isMapInitialized, setIsMapInitialized] = useState(false);

  // Get deals data using the data manager (only called once here)
  const { deals, isLoading: dealsLoading, error: dealsError } = useInfiniteDealsQuery();

  const { initPosition } = useGeolocation();
  initPosition();

  useEffect(() => {
    if (dealsError) {
      logger.error('Error fetching deals', { error: dealsError });
      showError('Error fetching deals. Please try again later.');
    }
  }, [dealsError]);

  // Map handlers
  const handleMapLoad = (map: google.maps.Map) => {
    logger.info('Map loaded successfully');
    // Set map instance for context
    setMapInstance(map);
    setIsMapInitialized(true);
  };

  const handleMarkerClick = (deal: Deal) => {
    setSelectedDeal(deal);
  };

  const handleCloseDealDetail = () => {
    setSelectedDeal(null);
  };

  if (isPending) {
    return <LoadingScreen message="Loading deals..." />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <GoogleMapsContext.Provider value={{ mapInstance, isInitialized: isMapInitialized }}>
      <AppLayout background="coffee">
        {/* Main container with flexible layout */}
        <MuiBox
          sx={{
            height: 'calc(100vh - 64px)',
            display: 'flex',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Map container - takes remaining space */}
          <MuiBox
            sx={{
              flex: 1,
              position: 'relative',
              zIndex: 1,
              marginRight: sidebarOpen && !isMobile ? '400px' : 0, // Add margin when sidebar is open on desktop
              transition: 'margin-right 0.3s ease', // Smooth transition
            }}
          >
            <GoogleMapsWrapper>
              <DealsMap
                deals={deals ?? []}
                onMarkerClick={handleMarkerClick}
                onCloseDealDetail={handleCloseDealDetail}
                onMapLoad={handleMapLoad}
              />
            </GoogleMapsWrapper>

            {/* Loading Overlay */}
            {dealsLoading && (
              <MuiBox
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(2px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2000,
                  ...(isMobile && {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  }),
                }}
              >
                <LoadingScreen message="Loading deals..." />
              </MuiBox>
            )}
          </MuiBox>

          {/* Layout-specific overlays positioned relative to map container */}
          <MuiBox
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              pointerEvents: 'none', // Allow map interactions to pass through
              '& > *': {
                pointerEvents: 'auto', // Re-enable pointer events for overlay children
              },
            }}
          >
            {/* Layout components now consume from store instead of calling useDealsQuery */}
            {isMobile ? <DealsMobileLayout /> : <DealsDesktopLayout />}
          </MuiBox>
        </MuiBox>
      </AppLayout>
    </GoogleMapsContext.Provider>
  );
}
